import { useEffect, useState, useRef } from "react";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import Breadcrumbs from "../components/Breadcrumbs";
import axios from "axios";
import useSearchedProducts from "../hooks/useSearchedProducts";

function Accessories() {
  const [accessories, setAccessories] = useState([]);
  const [filteredAccessories, setFilteredAccessories] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
  const { selectedAccessories, addAccessory, removeAccessory } = useCart();

  // ✅ ref for the product grid
  const gridRef = useRef(null);

  // Fetch all accessories
  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/products`);
        const allProducts = res.data;

        const filtered = allProducts.filter(
          (product) => product.category === "accessory"
        );

        setAccessories(filtered);
        setFilteredAccessories(filtered);
      } catch (err) {
        console.error("Error fetching accessories:", err);
      }
    };

    fetchAccessories();
  }, [BASE_URL]);

  // Hook to manage search results
  const { filteredProducts, isFiltered, resetProducts } =
    useSearchedProducts(filteredAccessories);

  // Current products for the active page
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Add/remove accessory
  const handleAddAccessory = (product) => {
    const exists = selectedAccessories.find((item) => item._id === product._id);
    if (exists) {
      removeAccessory(product._id);
    } else {
      addAccessory(product);
    }
  };

  // View product in modal
  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  // Filter by category
  const handleFilter = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset page on filter

    if (category === "All") {
      setFilteredAccessories(accessories);
    } else {
      setFilteredAccessories(
        accessories.filter(
          (product) => product.gender?.toLowerCase() === category.toLowerCase()
        )
      );
    }

    // ✅ Scroll to top of grid on filter
    if (gridRef.current) gridRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // Reset search
  const handleResetProducts = () => {
    resetProducts();
    setFilteredAccessories(accessories);
    setSelectedCategory("All");
    setCurrentPage(1);

    // ✅ Scroll to top of grid on reset
    if (gridRef.current) gridRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // ✅ Scroll to top of grid on page change
  const handlePageChange = (pageNum) => {
    setCurrentPage(pageNum);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div>
      <div className="max-w-6xl mx-auto px-4">
        <Breadcrumbs />
      </div>

      {/* Hero Section */}
      <section className="bg-blue-50 py-20 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4 text-blue-700">
            Accessories Collection
          </h1>
          <p className="text-lg text-gray-600">
            Explore our full range of premium and designer accessories to build
            your perfect hamper.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section className="px-4 sm:px-6 py-16 max-w-6xl mx-auto" data-aos="fade-up">
        {/* Categories */}
        {!isFiltered && (
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {["All", "Male", "Female", "Unisex"].map((label, idx) => (
              <button
                key={idx}
                onClick={() => handleFilter(label)}
                className={`px-4 py-2 border rounded-full text-sm transition ${
                  selectedCategory === label
                    ? "bg-blue-600 text-white"
                    : "hover:bg-blue-600 hover:text-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Show All Button when filtered */}
        {isFiltered && (
          <div className="flex justify-center mb-12">
            <button
              onClick={handleResetProducts}
              className="px-4 py-2 text-sm font-medium text-blue-600 border border-blue-400 rounded-full hover:bg-blue-600 hover:text-white transition"
            >
              Show All Accessories
            </button>
          </div>
        )}

        {/* Product Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 min-h-[400px]" // ✅ add min-height to prevent double scroll
        >
          {currentProducts.map((product, i) => (
            <div key={product._id} className="w-full">
              <ProductCard
                product={product}
                index={i}
                onClick={() => handleAddAccessory(product)}
                selected={selectedAccessories.some(
                  (item) => item._id === product._id
                )}
                onView={() => handleViewProduct(product)}
              />
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-wrap justify-center mt-6 gap-2">
            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`px-3 py-1 border rounded ${
                    pageNum === currentPage ? "bg-blue-600 text-white" : ""
                  }`}
                  onClick={() => handlePageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {/* Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddAccessory}
        selectedItems={selectedAccessories}
      />

      {/* Footer */}
      <div data-aos="fade-up">
        <Footer />
      </div>
    </div>
  );
}

export default Accessories;
